import {Component, OnInit, HostListener, DestroyRef, inject, Input} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DatePipe } from '@angular/common';
import {combineLatestWith, Observable} from 'rxjs';
import { map, filter, take, tap, withLatestFrom, startWith } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemAttribute} from '@emm/core/models/item-attribute.model';
import {ItemType} from '@emm/core/models/item-type.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ItemTypesFacadeService} from '@emm/management/services/item-types-facade.service';
import {NotificationService} from '@emm/shared/services/notification.service';

@Component({
    selector: 'emm-item-types-form',
    styleUrls: ['./item-types-form.component.scss'],
    templateUrl: './item-types-form.component.html',
    standalone: false
})
export class ItemTypesFormComponent implements OnInit {

    // todo: more clean up to do for properties..

    itemTypeDetail$ = this.itemTypesFacade.itemTypeDetail$;
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    canEdit: boolean;

    attributes$ = this.itemTypeDetail$.pipe(
        filter(RA.isNotNil),
        tap(),
        map((item: any) => item.attributes),
        startWith([] as ItemAttribute[])
    );

    itemTypeClasses$ = this.itemTypesFacade.itemTypeClasses$;
    isReadOnly$ = this.authFacade.isReadOnly$;

    disabled$ = this.isReadOnly$.pipe(combineLatestWith(this.itemTypesFacade.anyLoading$),
        map((sources: boolean[]) => R.any(R.equals(true), sources))
    );

    pipe = new DatePipe('en-US');
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    model$ = this.itemTypeDetail$.pipe(
        filter(RA.isNotNil),
        map((itemType: ItemType) => (this.form = this.formBuilder.group({
            name: [itemType.name, {disabled: !this.canEdit, nonNullable: true}],
            classTag: [itemType.classTag, {disabled: !this.canEdit, nonNullable: true}],
            className: [{value: itemType.className, disabled: true, nonNullable: true}],
            description: [{value: itemType.description, disabled: !this.canEdit}],
        })))
    );

    form;
    itemType;

    constructor(
        private authFacade: AuthFacadeService,
        private itemTypesFacade: ItemTypesFacadeService,
        private notificationService: NotificationService,
        private formBuilder: FormBuilder
    ) {}


    ngOnInit(): void {
        this.canEdit = this.isSuperAdmin || this.isAdmin;
        this.model$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (!this.canEdit) {
                this.form.disable();
            }
        });
    }


    cancel(): void {
        this.itemTypeDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (itemTypeDetail: ItemType) => this.itemType = itemTypeDetail);
        const model = {
            name: this.itemType.name,
            className: this.itemType.className,
            classTag: this.itemType.classTag,
            description: this.itemType.description,
            tag: this.itemType.tag,
        }
        this.form.reset(model);
    }



    submit(tag: string, reloadAfterUpdate: boolean = true): void {

        const formData = {
            classTag: this.form.controls.classTag.value,
            description: this.form.controls.description.value,
            name: this.form.controls.name.value,
            tag,
        };

        this.itemTypesFacade
            .updateItemType(tag, formData, reloadAfterUpdate)
            .pipe(withLatestFrom(this.itemTypeDetail$), takeUntilDestroyed(this.destroyRef))
            .subscribe(([success, itemType]) => {
                if (success) {
                    if (this.form.updateInitialValue) {
                        this.form.updateInitialValue();
                    }
                    this.notificationService.success('Saved : ' + itemType.name);
                    return;
                }
                this.notificationService.error('Could not save : ' + itemType.name);
            });
    }



    hasChanges(): boolean {
        return this.form && this.form.dirty;
    }



    isFormReady(): boolean {
        return this.form && this.form.dirty && !this.form.invalid;
    }



}
